/**
 * ComDesk's own menu, for the ComDesk section of the back-office navigation.
 *
 * ComDesk publishes its surfaces at `<basePath>/bizzmatix/menu.json` (SEAM §5,
 * ComDesk 3.1.0+). Reading it rather than listing the surfaces by hand means a
 * surface a later ComDesk version adds shows up in the menu with no change
 * here. Until it has loaded, or if ComDesk is down, the list in
 * config/backOfficeNav.ts stands in. The manifest's `home` comes first.
 */
import { useEffect, useState } from "react";

import type { BackOfficePage } from "@/config/backOfficeNav";

export const COMDESK_BASE = "/backoffice/comdesk";

interface Manifest {
  home?: unknown;
  items?: unknown;
}

let cached: Promise<BackOfficePage[] | null> | null = null;

/** Turns the manifest into menu pages; null for anything that is not one. */
export function pagesFromManifest(manifest: Manifest): BackOfficePage[] | null {
  if (!Array.isArray(manifest.items)) return null;
  const items = manifest.items.filter(
    (item): item is { label: string; path: string } =>
      typeof item?.label === "string" &&
      typeof item?.path === "string" &&
      /^[a-z0-9-]+$/.test(item.path),
  );
  if (items.length === 0) return null;
  const home = items.findIndex((item) => item.path === manifest.home);
  if (home > 0) items.unshift(...items.splice(home, 1));
  return items.map((item) => ({
    label: item.label,
    path: `/admin/comdesk/${item.path}`,
    available: true,
  }));
}

function load(): Promise<BackOfficePage[] | null> {
  cached ??= fetch(`${COMDESK_BASE}/bizzmatix/menu.json`, {
    credentials: "same-origin",
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((manifest) => (manifest ? pagesFromManifest(manifest) : null))
    .catch(() => null);
  return cached;
}

/** The manifest's pages once loaded, null before or without it. */
export function useComDeskMenu(enabled: boolean): BackOfficePage[] | null {
  const [pages, setPages] = useState<BackOfficePage[] | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void load().then((loaded) => {
      if (live) setPages(loaded);
    });
    return () => {
      live = false;
    };
  }, [enabled]);
  return pages;
}
